import useCurrentPage from '../hooks/use-set-current-page';
import { useEffect, useState } from 'react';
import Container from '../components/Container';
import PageHeading from '../components/PageHeadings';
import SubscriptionEditorGrid from '../components/SubscriptionEditor/SubscriptionEditorGrid';
import { IUserData } from '../types/user-info';
import useApiCaller from '../hooks/use-api-caller';
import { toast } from 'react-toastify';
import Loading from '../components/Loading';
import TabSelector from '../components/TabSelector';
import {
  AlertTypeKey,
  IDigestAlertPreference,
  IAdminDigestAlertPreference,
  ISmartAlertPreference,
  IAdminSmartAlertPreference,
  ICustomAlertPreference,
  IUsersEmailPreferences,
} from '../types/api-caller';
import { AlertSectionSettings } from '../components/SubscriptionEditor/AlertSettingsSection';

interface ICustomAlertsUpdatePayload {
  requestPayload: ICustomAlertPreference[];
  optimisticPayload: ICustomAlertPreference[];
}

interface ICustomAlertsUpdateOptions {
  refreshOnSuccess?: boolean;
}

interface IAlertListFields {
  list_ids?: number[] | null;
  lists?: number[] | null;
}

export default function SubscriptionEditor() {
  const currentPage = useCurrentPage();

  const [userData, setUserData] = useState<IUserData[]>([]);
  const [emailDomains, setEmailDomains] = useState<Set<string>>(new Set());
  const [selectedTab, setSelectedTab] = useState<string>('');
  const [selectedUserIds, setSelectedUserIds] = useState<Set<number>>(new Set());
  const [searching, setSearching] = useState<boolean>(true);
  const [isCustomAlertsSyncing, setIsCustomAlertsSyncing] = useState<boolean>(false);

  const filteredRows = userData.filter((user) => {
    const domain = user.email.includes('@') ? user.email.split('@')[1].split('.')[0] : '';
    return domain.toLowerCase() === selectedTab;
  });

  const apiCaller = useApiCaller();

  const cloneAlerts = (alerts: ICustomAlertPreference[]): ICustomAlertPreference[] => {
    return alerts.map((alert) => ({
      ...alert,
      list_ids: Array.isArray(alert.list_ids) ? [...alert.list_ids] : [],
    }));
  };

  const toApiAlertPayload = (alerts: ICustomAlertPreference[]): ICustomAlertPreference[] => {
    return cloneAlerts(alerts).map((alert) => ({
      ...alert,
      // Admin flow sends full custom-alert config array; API recreates alerts from config.
      alert_id: 0,
    }));
  };

  const createEmailPreferencesPayload = (
    userIds: number[],
    alertType: AlertTypeKey,
    payload: IUsersEmailPreferences['settings']
  ): IUsersEmailPreferences => {
    const basePayload: IUsersEmailPreferences = {
      user_ids: userIds,
      settings: [],
    };

    if (alertType === 'DIGEST') {
      return {
        ...basePayload,
        settings: payload as IAdminDigestAlertPreference,
      };
    }

    if (alertType === 'SMART') {
      return {
        ...basePayload,
        settings: payload as IAdminSmartAlertPreference,
      };
    }

    return {
      ...basePayload,
      settings: payload as ICustomAlertPreference[],
    };
  };

  const resolveListIds = (alert?: IAlertListFields | null): number[] => {
    if (Array.isArray(alert?.list_ids)) return [...alert.list_ids];
    if (Array.isArray(alert?.lists)) return [...alert.lists];
    return [];
  };

  const buildDigestPreference = (
    existingDigest: IDigestAlertPreference | null | undefined,
    updatedSettings: AlertSectionSettings
  ): IDigestAlertPreference => {
    return {
      enabled: updatedSettings.enabled,
      frequency: (updatedSettings.frequency ?? existingDigest?.frequency ?? 'DAILY').toUpperCase(),
      scope_type: existingDigest?.scope_type || 'PORTFOLIO',
      exclude_low_articles: updatedSettings.exclude_low_articles,
      list_ids: resolveListIds(existingDigest),
    };
  };

  const buildSmartPreference = (
    existingSmart: ISmartAlertPreference | null | undefined,
    updatedSettings: AlertSectionSettings
  ): ISmartAlertPreference => {
    return {
      enabled: updatedSettings.enabled,
      scope_type: existingSmart?.scope_type || 'PORTFOLIO',
      exclude_low_articles: updatedSettings.exclude_low_articles,
      score_increase_enabled: updatedSettings.score_increase_enabled ?? existingSmart?.score_increase_enabled ?? true,
      score_decrease_enabled: updatedSettings.score_decrease_enabled ?? existingSmart?.score_decrease_enabled ?? false,
      list_ids: resolveListIds(existingSmart),
    };
  };

  const toDigestUpdateSettings = (alert: IDigestAlertPreference): IAdminDigestAlertPreference => {
    return {
      enabled: alert.enabled,
      frequency: alert.frequency,
      exclude_low_articles: alert.exclude_low_articles,
    };
  };

  const toSmartUpdateSettings = (alert: ISmartAlertPreference): IAdminSmartAlertPreference => {
    return {
      enabled: alert.enabled,
      exclude_low_articles: alert.exclude_low_articles,
      score_increase_enabled: alert.score_increase_enabled,
      score_decrease_enabled: alert.score_decrease_enabled,
    };
  };

  const shouldNormalizeBatchScope = (selectedUsersCount: number): boolean => selectedUsersCount >= 2;

  const normalizeCustomAlertsForBatch = (alerts: ICustomAlertPreference[], normalizeBatchScope: boolean): ICustomAlertPreference[] => {
    const clonedAlerts = cloneAlerts(alerts);
    if (!normalizeBatchScope) return clonedAlerts;

    return clonedAlerts.map((alert) => ({
      ...alert,
      scope_type: 'PORTFOLIO',
      list_ids: [],
    }));
  };

  const handleUserPreferenceUpdate = (selectedUserIds: Set<number>, alertType: AlertTypeKey, updatedSettings: AlertSectionSettings) => {
    const selectedUsers = userData.filter((user) => selectedUserIds.has(user.id));
    if (selectedUsers.length === 0) return;

    const fallbackUser = selectedUsers[0];
    const normalizeBatchScope = shouldNormalizeBatchScope(selectedUsers.length);
    let requestSettings: IUsersEmailPreferences['settings'];
    let customOptimisticPayload: ICustomAlertPreference[] = [];

    if (alertType === 'DIGEST') {
      const digestPayload = buildDigestPreference(fallbackUser.digest, updatedSettings);
      requestSettings = toDigestUpdateSettings(digestPayload);
    } else if (alertType === 'SMART') {
      const smartPayload = buildSmartPreference(fallbackUser.smart, updatedSettings);
      requestSettings = toSmartUpdateSettings(smartPayload);
    } else {
      const existingCustom = fallbackUser.custom?.[0];
      customOptimisticPayload = normalizeCustomAlertsForBatch(
        [
          {
            name: updatedSettings.name ?? existingCustom?.name ?? 'Custom Alert',
            alert_id: existingCustom?.alert_id ?? 0,
            enabled: updatedSettings.enabled,
            increase_threshold: updatedSettings.increase_threshold,
            decrease_threshold: updatedSettings.decrease_threshold,
            frequency: (updatedSettings.frequency ?? existingCustom?.frequency ?? 'DAILY').toUpperCase(),
            scope_type: existingCustom?.scope_type || 'PORTFOLIO',
            exclude_low_articles: updatedSettings.exclude_low_articles,
            list_ids: resolveListIds(existingCustom),
          } as ICustomAlertPreference,
        ],
        normalizeBatchScope
      );

      requestSettings = customOptimisticPayload;
    }

    const requestPayload = createEmailPreferencesPayload(
      selectedUsers.map((user) => user.id),
      alertType,
      requestSettings
    );

    void apiCaller
      .updateUserEmailPreferences(alertType, requestPayload)
      .then((status) => {
        if (status !== 'Updated') {
          throw new Error(`Unexpected updateUserEmailPreferences status: ${status}`);
        }

        setUserData((prev) =>
          prev.map((prevUser) => {
            if (!selectedUserIds.has(prevUser.id)) return prevUser;
            if (alertType === 'DIGEST') return { ...prevUser, digest: buildDigestPreference(prevUser.digest, updatedSettings) };
            if (alertType === 'SMART') return { ...prevUser, smart: buildSmartPreference(prevUser.smart, updatedSettings) };
            return { ...prevUser, custom: customOptimisticPayload };
          })
        );

        toast.success(`${alertType} preferences updated for ${selectedUsers.length} user${selectedUsers.length > 1 ? 's' : ''}`);
      })
      .catch((error) => {
        console.error(error);
        toast.error(`Failed to update ${alertType} preferences for ${selectedUsers.length} user${selectedUsers.length > 1 ? 's' : ''}`);
        void loadData({ showLoader: false });
      });
  };

  // Admin Custom Alert CRUD Handlers

  const runCustomAlertsUpdate = (
    selectedUserIds: Set<number>,
    buildPayloadForUser: (user: IUserData) => ICustomAlertsUpdatePayload,
    successMessage: string,
    options: ICustomAlertsUpdateOptions = {}
  ) => {
    const selectedUsers = userData.filter((user) => selectedUserIds.has(user.id));
    if (selectedUsers.length === 0) return;

    const fallbackUser = selectedUsers[0];
    const normalizeBatchScope = shouldNormalizeBatchScope(selectedUsers.length);
    const payloads = buildPayloadForUser(fallbackUser);
    const requestPayload = toApiAlertPayload(normalizeCustomAlertsForBatch(payloads.requestPayload, normalizeBatchScope));
    const optimisticPayload = toApiAlertPayload(normalizeCustomAlertsForBatch(payloads.optimisticPayload, normalizeBatchScope));
    const selectedIds = selectedUsers.map((user) => user.id);

    const requestBody = createEmailPreferencesPayload(selectedIds, 'CUSTOM', requestPayload);

    setUserData((prev) =>
      prev.map((user) => {
        if (!selectedUserIds.has(user.id)) return user;

        return {
          ...user,
          custom: optimisticPayload,
        };
      })
    );

    void apiCaller
      .updateUserEmailPreferences('CUSTOM', requestBody)
      .then((status) => {
        if (status !== 'Updated') {
          throw new Error(`Unexpected updateUserEmailPreferences status: ${status}`);
        }

        toast.success(successMessage);

        if (options.refreshOnSuccess) {
          setIsCustomAlertsSyncing(true);
          void loadData({ showLoader: false }).finally(() => {
            setIsCustomAlertsSyncing(false);
          });
        }
      })
      .catch((error) => {
        console.error(error);
        toast.error(`Failed to update custom alerts for ${selectedUsers.length} user${selectedUsers.length > 1 ? 's' : ''}`);
        void loadData({ showLoader: false });
      });
  };

  const handleAdminCreateCustomAlert = (selectedUserIds: Set<number>, newAlert: ICustomAlertPreference) => {
    runCustomAlertsUpdate(
      selectedUserIds,
      (user) => {
        const existing = cloneAlerts(user.custom ?? []);
        const newAdminAlert: ICustomAlertPreference = {
          ...newAlert,
          alert_id: 0,
        };

        return {
          requestPayload: [...existing, newAdminAlert],
          optimisticPayload: [...existing, newAdminAlert],
        };
      },
      `Custom alert created for ${userData.filter((u) => selectedUserIds.has(u.id)).length} user(s)`,
      { refreshOnSuccess: true }
    );
  };

  const handleAdminUpdateCustomAlert = (
    selectedUserIds: Set<number>,
    sourceIndex: number,
    previousAlertName: string,
    updatedAlert: ICustomAlertPreference
  ) => {
    runCustomAlertsUpdate(
      selectedUserIds,
      (user) => {
        const existing = cloneAlerts(user.custom ?? []);
        const hasValidSourceIndex = sourceIndex >= 0 && sourceIndex < existing.length;
        const normalizedPreviousAlertName = previousAlertName.trim();
        const matchAndMergeAlert = (a: ICustomAlertPreference) => ({
          ...a,
          ...updatedAlert,
          alert_id: 0,
        });

        let mergedAlerts = existing;

        if (hasValidSourceIndex) {
          mergedAlerts = existing.map((a, idx) => (idx === sourceIndex ? matchAndMergeAlert(a) : a));
        } else {
          const matchedByPreviousName =
            normalizedPreviousAlertName.length > 0 && existing.some((a) => a.name === normalizedPreviousAlertName);

          if (matchedByPreviousName) {
            mergedAlerts = existing.map((a) => (a.name === normalizedPreviousAlertName ? matchAndMergeAlert(a) : a));
          } else {
            mergedAlerts = existing.map((a) => (a.name === updatedAlert.name ? matchAndMergeAlert(a) : a));
          }
        }

        return {
          requestPayload: mergedAlerts,
          optimisticPayload: mergedAlerts,
        };
      },
      `Custom alert updated for ${userData.filter((u) => selectedUserIds.has(u.id)).length} user(s)`
    );
  };

  const handleAdminDeleteCustomAlert = (selectedUserIds: Set<number>, alertName: string) => {
    runCustomAlertsUpdate(
      selectedUserIds,
      (user) => {
        const existing = cloneAlerts(user.custom ?? []);
        const mergedAlerts = existing.filter((a) => a.name !== alertName);

        return {
          requestPayload: mergedAlerts,
          optimisticPayload: mergedAlerts,
        };
      },
      `Custom alert deleted for ${userData.filter((u) => selectedUserIds.has(u.id)).length} user(s)`
    );
  };

  const handleAdminToggleCustomAlert = (selectedUserIds: Set<number>, alertName: string, enabled: boolean) => {
    runCustomAlertsUpdate(
      selectedUserIds,
      (user) => {
        const existing = cloneAlerts(user.custom ?? []);
        const mergedAlerts = existing.map((a) => {
          if (a.name === alertName) return { ...a, enabled };
          return a;
        });

        return {
          requestPayload: mergedAlerts,
          optimisticPayload: mergedAlerts,
        };
      },
      `Custom alert ${enabled ? 'enabled' : 'disabled'} for ${userData.filter((u) => selectedUserIds.has(u.id)).length} user(s)`
    );
  };

  const handleAdminReplaceCustomAlerts = (selectedUserIds: Set<number>, alerts: ICustomAlertPreference[]) => {
    const mergedAlerts = cloneAlerts(alerts);

    runCustomAlertsUpdate(
      selectedUserIds,
      () => {
        return {
          requestPayload: mergedAlerts,
          optimisticPayload: mergedAlerts,
        };
      },
      `Custom alerts merged for ${userData.filter((u) => selectedUserIds.has(u.id)).length} user(s)`
    );
  };

  const handleAdminToggleAllCustomAlerts = (selectedUserIds: Set<number>, enabled: boolean) => {
    runCustomAlertsUpdate(
      selectedUserIds,
      (user) => {
        const mergedAlerts = cloneAlerts(user.custom ?? []).map((a) => ({ ...a, enabled }));

        return {
          requestPayload: mergedAlerts,
          optimisticPayload: mergedAlerts,
        };
      },
      `Custom alerts ${enabled ? 'enabled' : 'disabled'} for ${userData.filter((u) => selectedUserIds.has(u.id)).length} user(s)`
    );
  };
  // --- End Admin Custom Alert CRUD Handlers ---

  const loadData = ({ showLoader = true }: { showLoader?: boolean } = {}) => {
    if (showLoader) {
      setSearching(true);
    }

    return apiCaller
      .getAllUsers()
      .then((usersResponse: IUserData[]) => {
        // extract all email domains
        const domains = new Set<string>();
        usersResponse.forEach((user) => {
          const domain = user.email.includes('@') ? user.email.split('@')[1].split('.')[0] : '';
          if (domain) domains.add(domain.toLowerCase());
        });
        const sortedDomains = Array.from(domains).sort((a, b) => a.localeCompare(b));
        setEmailDomains(new Set(sortedDomains));
        setSelectedTab((currentTab) => {
          if (sortedDomains.length === 0) return '';
          if (currentTab && sortedDomains.includes(currentTab)) return currentTab;
          return sortedDomains[0];
        });

        // sort rows by name, email
        usersResponse.sort((a, b) => {
          const nameA = a.name.toLowerCase();
          const nameB = b.name.toLowerCase();
          if (nameA !== nameB) return nameA.localeCompare(nameB);

          const emailA = a.email.toLowerCase();
          const emailB = b.email.toLowerCase();
          return emailA.localeCompare(emailB);
        });

        const validUserIds = new Set(usersResponse.map((user) => user.id));
        setSelectedUserIds((previous) => {
          const next = new Set(Array.from(previous).filter((userId) => validUserIds.has(userId)));
          return next;
        });

        setUserData(usersResponse);
      })
      .catch((err) => {
        console.error(err);
        toast.error('Fetching email preferences failed');
      })
      .finally(() => {
        if (showLoader) {
          setSearching(false);
        }
      });
  };

  const handleTabChange = (tab: string) => {
    setSelectedTab(tab);
    setSelectedUserIds(new Set());
  };

  useEffect(() => {
    void loadData();
    currentPage.setCurrentPage('SubscriptionEditor');
  }, []);

  return (
    <Container>
      <div className="lg:flex lg:justify-center lg:w-full">
        <div className="lg:w-full lg:bg-white lg:p-8">
          <PageHeading title="Email Subscription Editor" />
          {searching && <Loading />}
          {!searching && userData.length > 0 && filteredRows.length > 0 && (
            <div className="flex flex-col gap-4">
              <div className={`text-md whitespace-pre-line overflow-x-auto text-gray-500 content-center`}>
                Email subscriptions can be managed for all users in Default Settings or set per user in User Settings.
              </div>
              <TabSelector
                options={[...Array.from(emailDomains).map((domain) => ({ label: domain, value: domain }))]}
                selected={selectedTab}
                onChange={handleTabChange}
              />
              <SubscriptionEditorGrid
                data={filteredRows}
                selectedUserIds={selectedUserIds}
                onSelectedUserIdsChange={setSelectedUserIds}
                onPreferenceUpdate={handleUserPreferenceUpdate}
                onAdminCreateCustomAlert={handleAdminCreateCustomAlert}
                onAdminUpdateCustomAlert={handleAdminUpdateCustomAlert}
                onAdminDeleteCustomAlert={handleAdminDeleteCustomAlert}
                onAdminToggleCustomAlert={handleAdminToggleCustomAlert}
                onAdminReplaceCustomAlerts={handleAdminReplaceCustomAlerts}
                onAdminToggleAllCustomAlerts={handleAdminToggleAllCustomAlerts}
                isCustomAlertsSyncing={isCustomAlertsSyncing}
              />
            </div>
          )}
        </div>
      </div>
    </Container>
  );
}
